import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, forwardRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { countries } from 'countries-list';
import { debounceTime, map, startWith, tap } from 'rxjs';

import { FlagIconComponent } from '../../flag-icon';

export interface CountryOption {
  code: string;
  name: string;
  callingCode: string;
}

export const COUNTRY_OPTIONS: CountryOption[] = Object.entries(countries)
  .map(([code, data]) => ({
    code,
    name: data.name,
    callingCode: data.phone[0].toString(),
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

@Component({
  selector: 'rms-country-select',
  templateUrl: './country-select.component.html',
  standalone: true,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    FlagIconComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CountrySelectComponent),
      multi: true,
    }
  ],
})
export class CountrySelectComponent implements OnInit, ControlValueAccessor {
  @Input() public placeholder = '';

  private readonly destroyRef = inject(DestroyRef);

  protected control = new FormControl<CountryOption | string | null>(null);
  protected filteredCountries$ = this.control.valueChanges.pipe(
    startWith(this.control.value),
    debounceTime(150),
    map((value) => {
      const search = typeof value === 'string' ? value.toLowerCase() : '';

      return search
        ? COUNTRY_OPTIONS.filter((o) => o.name.toLowerCase().includes(search) || o.code.toLowerCase().includes(search))
        : COUNTRY_OPTIONS;
    })
  );

  private onChange?: (value: CountryOption | null) => void;
  private onTouched?: () => void;

  public ngOnInit(): void {
    this.control.valueChanges.pipe(
      tap((value) => {
        this.onChange?.(value && typeof value === 'object' ? value : null);
        this.onTouched?.();
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  public displayCountry = (option: CountryOption | string | null): string => {
    if (!option || typeof option === 'string') return option ?? '';

    return option.name;
  };

  public writeValue(countryOption: Partial<CountryOption> | null): void {
    const option = countryOption?.code
      ? (COUNTRY_OPTIONS.find((o) => o.code === countryOption.code) ?? null)
      : countryOption?.name
        ? (COUNTRY_OPTIONS.find((o) => o.name === countryOption?.name) ?? null)
        : null;

    this.control.setValue(option, { emitEvent: false });
  }

  public registerOnChange(fn: (value: CountryOption | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.control.disable({ emitEvent: false }) : this.control.enable({ emitEvent: false });
  }
}
